import { Link } from "~/components/Link"
import { ArrowRightIcon, PlusIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { EmptyState, LoadingState } from "~/components/screens/StateScreen"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import { Card } from "~/components/ui/card"
import { Search } from "~/components/ui/search"
import { ScrollArea } from "~/components/ui/scroll-area"
import { Switch } from "~/components/ui/switch"
import { useTeam } from "~/hooks/useTeam"
import { cn, shortRelativeTime } from "~/lib/utils"
import { api } from "~/trpc/react"

export default function AutomationsPage() {
	const { team, isLoading, orgSlug } = useTeam()
	const utils = api.useUtils()
	const [search, setSearch] = useState("")
	const rulesQuery = api.automations.list.useQuery(
		{ teamId: team?.id ?? "" },
		{ enabled: Boolean(team?.id && team.automationsFeatureEnabled) }
	)
	const enabledMutation = api.automations.setEnabled.useMutation()

	const rules = useMemo(() => {
		const needle = search.trim().toLowerCase()
		if (!needle) return rulesQuery.data ?? []
		return (rulesQuery.data ?? []).filter((rule) =>
			[rule.name, rule.description, rule.triggerLabel]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(needle))
		)
	}, [rulesQuery.data, search])

	if (isLoading) return <LoadingState />
	if (!team) return <EmptyState title="Team not found." />
	if (!team.automationsFeatureEnabled) {
		return <EmptyState title="Automations unavailable" />
	}
	if (rulesQuery.isLoading) return <LoadingState />

	const newHref = `/${orgSlug}/${team.slug}/automations/new`

	return (
		<main className="flex h-full min-h-0 flex-col">
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={
					<>
						<Search
							value={search}
							onChange={setSearch}
							placeholder="Search automations"
						/>
						<Button asChild size="default" shape="rounded">
							<Link to={newHref}>
								<PlusIcon className="size-4" /> New automation
							</Link>
						</Button>
					</>
				}
			>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<span className="font-medium text-text-main">Automations</span>
			</AppHeader>

			<ScrollArea className="min-h-0 flex-1">
				<div className="grid w-full gap-3 px-3 py-4 sm:px-5 lg:px-6">
					{rules.length ? (
						rules.map((rule) => {
							const href = `/${orgSlug}/${team.slug}/automations/${rule.id}`
							return (
								<Card
									key={rule.id}
									variant="interactive"
									className={cn(
										"group gap-4 p-4",
										!rule.enabled && "opacity-65"
									)}
								>
									<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
										<Link to={href} className="min-w-0">
											<div className="mb-2 flex flex-wrap items-center gap-2">
												<span className="rounded-full bg-active px-2 py-1 text-xs font-semibold text-text-main">
													{rule.enabled ? "Enabled" : "Disabled"}
												</span>
												<span className="text-xs text-text-small">
													{rule.executionMode.toLowerCase()} execution
												</span>
											</div>
											<h2 className="truncate text-base font-semibold text-text-main">
												{rule.name}
											</h2>
											{rule.description ? (
												<p className="mt-1 line-clamp-2 max-w-3xl text-sm text-text-sub">
													{rule.description}
												</p>
											) : null}
											<div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-text-small">
												<span className="rounded-lg bg-background-secondary px-2 py-1">
													When {rule.triggerLabel}
												</span>
												<ArrowRightIcon className="size-3" />
												<span className="rounded-lg bg-background-secondary px-2 py-1">
													Run configured actions
												</span>
											</div>
										</Link>
										<div className="flex items-center justify-between gap-4 lg:justify-end">
											<div className="text-right text-xs text-text-small">
												<p>Last ran</p>
												<p className="font-medium text-text-sub">
													{rule.lastRanAt
														? shortRelativeTime(rule.lastRanAt)
														: "Never"}
												</p>
											</div>
											<Switch
												checked={rule.enabled}
												disabled={enabledMutation.isPending}
												aria-label={`${rule.enabled ? "Turn off" : "Turn on"} ${rule.name}`}
												onCheckedChange={async (enabled) => {
													await enabledMutation.mutateAsync({
														teamId: team.id,
														ruleId: rule.id,
														enabled
													})
													await utils.automations.list.invalidate({
														teamId: team.id
													})
													toast.success(
														enabled
															? "Automation turned on"
															: "Automation turned off"
													)
												}}
											/>
										</div>
									</div>
								</Card>
							)
						})
					) : (
						<EmptyState
							scope="inline"
							title={
								search.trim()
									? "No automations match that search."
									: "No automations yet. Create one to start automating routine work."
							}
						/>
					)}
				</div>
			</ScrollArea>
		</main>
	)
}
